'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import type { LightboxOptions, LightboxSlide, OpenLightboxArgs } from './lightbox.types';

// The lightbox is opened by a minority of visitors, so its bundle (script +
// stylesheet, ~290 KiB) must not block the first render of every page. The
// provider itself stays tiny — only state and context — and the real component
// is fetched on the first open (see AGENTS.md → Performance).
const LightboxRoot = dynamic(() => import('./LightboxRoot'), { ssr: false });

type Ctx = {
    openLightbox: (args: OpenLightboxArgs) => void;
    closeLightbox: () => void;
    isOpen: boolean;
};

const LightboxContext = createContext<Ctx | null>(null);

export function LightboxProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [slides, setSlides] = useState<LightboxSlide[]>([]);
    const [index, setIndex] = useState(0);
    const [options, setOptions] = useState<LightboxOptions | undefined>();
    // Stays true after the first open so re-opening is instant and the closing
    // animation still has a mounted component to run on.
    const [isMounted, setIsMounted] = useState(false);

    const openLightbox = useCallback(({ slides, index = 0, options }: OpenLightboxArgs) => {
        if (!slides.length) return;
        setSlides(slides);
        setIndex(index);
        setOptions(options);
        setIsMounted(true);
        setIsOpen(true);
    }, []);

    const closeLightbox = useCallback(() => {
        setIsOpen(false);
        setOptions(undefined);
        setSlides([]);
        setIndex(0);
    }, []);

    const value = useMemo(() => ({ openLightbox, closeLightbox, isOpen }), [openLightbox, closeLightbox, isOpen]);

    return (
        <LightboxContext.Provider value={value}>
            {children}
            {isMounted && (
                <LightboxRoot
                    open={isOpen}
                    index={index}
                    slides={slides}
                    options={options}
                    onIndexChange={setIndex}
                    onClose={closeLightbox}
                />
            )}
        </LightboxContext.Provider>
    );
}

export function useLightbox() {
    const ctx = useContext(LightboxContext);
    if (!ctx) throw new Error('useLightbox must be used within LightboxProvider');
    return ctx;
}
